import { Component, EventEmitter, Input, OnInit, Output, OnDestroy} from '@angular/core';
import { BusinessEntityService } from 'src/app/services/businessEntity.service';
import BusinessEntity from 'src/app/services/businessEntity.model';
import { FarmSiteService } from 'src/app/services/farmSite.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import { ItemMasterService } from 'src/app/services/itemMaster.service';
import ItemMasterModel from 'src/app/services/itemMaster.model';
import { TransactionTypeService } from 'src/app/services/transactionType.service';
import TransactionTypeModel from 'src/app/services/transactionType.model';
import { TransactionsService } from 'src/app/services/transactions.service';
import TransactionsModel, { Items } from 'src/app/services/transactions.model';
import UOMMasterModel from 'src/app/services/uom.model';
import { CustomerPondService } from 'src/app/services/customerPond.service';
import CustomerPondModel from 'src/app/services/customerPond.model';
import { VendorsService } from 'src/app/services/vendors.service';
import Vendors from 'src/app/services/vendor.model';
import { VendorDuesService } from 'src/app/services/vendorDues.service';
import VendorDuesModel from 'src/app/services/vendorDues.model';
import { PaymentTransactionService } from 'src/app/services/paymentTransaction.service';
import PaymentTransactionModel from 'src/app/services/paymentTransaction.model';
import * as _ from 'lodash';
import * as moment from 'moment';
import AttachmentsModel from 'src/app/services/attachments.model';
import AttachmentsService from 'src/app/services/attachements.service';
import {FormBuilder, FormControl, FormGroup } from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';

declare var $: any;

/**
 * @component MultiLinePurchaseComponent
 * @selector app-multi-line-purchase
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @description
 * The `MultiLinePurchaseComponent` facilitates the recording of multi-line purchase transactions for farm materials.
 * It allows users to add multiple items (e.g., feed, medicine) in a single transaction record, streamlining the
 * data entry process for bulk purchases.
 *
 * ## Key Responsibilities:
 * 1. **Multi-Item Entry**: Supports adding multiple line items dynamically to a single purchase transaction.
 * 2. **Data Retrieval**: Fetches necessary master data such as business entities, farm sites, items, and vendors.
 * 3. **Calculation**: Automatically calculates total amounts, including GST and unit costs, for all line items.
 * 4. **Payment Integration**: Integrates with the payment system to record payment details for the total purchase amount.
 * 5. **Validation**: Ensures data integrity through form validation for each line item and the overall transaction.
 * 6. **File Attachment**: Allows uploading supporting documents (e.g., invoices) for the transaction.
 * 7. **Role-Based Access**: Adapts initialization and data loading based on the user's role (Farmer, Farm Manager, Business Admin).
 *
 * ## User Roles & Permissions:
 * - **Farm Category Users**:
 *   - **Business Admin / Farmer**: Can record purchases for their farm business.
 *   - **Farm Manager**: Can record purchases for assigned farm sites.
 * - **Other Categories**:
 *   - **Employee**: Can record purchases for selected businesses (Admin flow).
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context.
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `loginBusinessName`: Used for display and context.
 * - `LoginUserName`: Used for tracking creator/updater.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` determines user context and loads initial data (vendors, items).
 * 2. **Input**: User adds line items, selecting item, UOM, quantity, and cost.
 * 3. **Calculation**: `totalAmount` updates the total transaction value as items are added or modified.
 * 4. **Submission**: `inventoryTransaction` validates all items and submits the transaction batch.
 * 5. **Payment**: `getPaymentTransaction` records the payment for the total amount.
 *
 * ## UI State Management:
 * - **Dynamic Rows**: Manages the `items` array to render rows for data entry.
 * - **Form**: `registerForm` handles overall transaction details validation.
 * - **Loading**: `submitButtonDisabled` prevents double submission.
 */
@Component({
  selector: 'app-multi-line-purchase',
  templateUrl: './multi-line-purchase.component.html',
  styleUrls: ['./multi-line-purchase.component.scss']
})
export class MultiLinePurchaseComponent implements OnInit, OnDestroy{
  /** @type {Array<BusinessEntity>} List of business entities (unused in current logic). */
  public businessEntities: Array<BusinessEntity>;

  /** @type {BusinessEntity} Single business entity model (unused in current logic). */
  public businessEntity: BusinessEntity;

  /** @type {Array<FarmSiteModel>} List of farm sites (unused in current logic). */
  public farmSites: Array<FarmSiteModel>;

  /** @type {Array<ItemMasterModel>} List of item masters (products) available for purchase. */
  public itemMasters: Array<ItemMasterModel>;

  /** @type {ItemMasterModel} Single item master model (unused in current logic). */
  public itemMaster: ItemMasterModel;

  /** @type {Array<TransactionTypeModel>} List of transaction types (unused in current logic). */
  public transactionTypes: Array<TransactionTypeModel>;

  /** @type {TransactionTypeModel} Single transaction type model (unused in current logic). */
  public transactionType: TransactionTypeModel;

  /** @type {Array<TransactionsModel>} List of transactions (unused in current logic). */
  public transactions: Array<TransactionsModel>;

  /** @type {TransactionsModel} Model used for binding transaction header details. */
  public transaction: TransactionsModel;

  /** @type {Array<UOMMasterModel>} List of UOMs (unused in current logic). */
  public uoms: Array<UOMMasterModel>;

  /** @type {UOMMasterModel} Single UOM model (unused in current logic). */
  public uom: UOMMasterModel;

  /** @type {Array<CustomerPondModel>} List of customer ponds (unused in current logic). */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {Array<Vendors>} List of vendors available for selection. */
  public vendors: Array<Vendors>;

  /** @type {Vendors} Single vendor model (unused in current logic). */
  public vendor: Vendors;

  /** @type {Array<VendorDuesModel>} List of vendor dues (unused in current logic). */
  public vendorDues: Array<VendorDuesModel>;

  /** @type {VendorDuesModel} Single vendor due model (unused in current logic). */
  public vendorDue: VendorDuesModel;

  /** @type {Array<PaymentTransactionModel>} List of payment transactions (unused in current logic). */
  public paymentTransactions: Array<PaymentTransactionModel>;

  /** @type {PaymentTransactionModel} Model for recording payment details. */
  public paymentTransaction: PaymentTransactionModel;

  /** @type {AttachmentsModel} Model for file attachments. */
  public mtlAttachmentsModel: AttachmentsModel;

  /** @type {Array<Items>} Array of line items for the purchase transaction. */
  public items: Array<Items>;

  /** @type {string} Role of the logged-in user. */
  public loginPersonType: string;

  /** @type {string} Name of the logged-in user. */
  public LoginUserName: string;

  /** @type {string} Name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {string} Category of the logged-in user's business. */
  public loginUserBusinessCategory: string;

  /** @type {boolean} Flag indicating if form submitted (unused). */
  public issubmitted: boolean;

  /** @type {File | any} Selected image file for upload. */
  public imageFile: File | any;

  /** @type {String | ArrayBuffer} Base64 string of the selected file. */
  public selectedbase64file: String | ArrayBuffer;

  /** @type {boolean} Flag indicating if form cleared. */
  public isCleared: boolean;

  /** @type {boolean} Flag indicating if a file is selected. */
  public isFileSelected: boolean;

  /** @type {Array<any>} List of attachments. */
  public attachments = new Array();

  /** @type {number} Total amount of the purchase transaction. */
  public totalamount: number;

  /** @type {any} Data of the latest created transaction batch. */
  public latestTransactionData: any;

  /** @type {any} Payment details object (unused). */
  public paymentDetails: any;

  /** @type {string} Input property for Business ID. */
  @Input('businessId') paramsbusinessId: string;

  /** @type {string} Input property for Site ID. */
  @Input('siteId') paramsfarmsiteId: string;

  /** @type {string} Input property for Business Name. */
  @Input('businessName') businessName: string;

  /** @type {string} Input property for Location Name. */
  @Input('locationName') locationName: string;

  /** @type {EventEmitter<any>} Output event to navigate back/forward (unused). */
  @Output() gotoMultiLine: EventEmitter<any> = new EventEmitter<any>();

  /** @type {any} Transaction date. */
  public transactionDate: any;

  /** @type {boolean} Flag indicating if form submitted. */
  public submitted = false;

  /** @type {Array<any>} List of inventory items (stockable products). */
  public inventoryItems = new Array();

  /** @type {string} Name of the selected file. */
  public fileName: string;

  /** @type {FormGroup} Main reactive form for transaction details. */
  public registerForm: FormGroup;

  /** @type {boolean} Flag controlling vendor dropdown visibility. */
  public vendorsDropdownOpen: boolean = false;

  /** @type {string} Search string for vendor name. */
  public vendorNameSearch: string;

  /** @type {any[]} Filtered vendor names for autocomplete. */
  public filteredVendorNames: any[] = [];

  /** @type {boolean} Flag to disable submit button during processing. */
  public submitButtonDisabled: boolean;

  /** @type {FormControl} Control for transaction date with validation. */
  public transactionDateControl = new FormControl(new Date());

  /** @type {Date} Maximum allowed date for transaction. */
  maxDate: Date = new Date();

  /**
   * Constructor for MultiLinePurchaseComponent.
   * Initializes services and models.
   *
   * @param {ToastrService} toasterService - Service for toast notifications.
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {AttachmentsService} mtlattachmentsService - Service for handling attachments.
   * @param {PaymentTransactionService} paymentTransactionservice - Service for payment transactions.
   * @param {VendorDuesService} vendorDuesService - Service for vendor dues (unused).
   * @param {VendorsService} vendorsService - Service for vendor operations.
   * @param {CustomerPondService} customerPondsService - Service for customer ponds (unused).
   * @param {ItemMasterService} itemMasterService - Service for item master operations.
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {FarmSiteService} farmSiteService - Service for farm site operations (unused).
   * @param {TransactionsService} transactionsService - Service for transaction operations.
   * @param {TransactionTypeService} transactionTypeService - Service for transaction types (unused).
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   *
   * @service ToastrService
   * @service FormBuilder
   * @service AttachmentsService
   * @service PaymentTransactionService
   * @service VendorDuesService
   * @service VendorsService
   * @service CustomerPondService
   * @service ItemMasterService
   * @service BusinessEntityService
   * @service FarmSiteService
   * @service TransactionsService
   * @service TransactionTypeService
   * @service CommonMethodsService
   */
  constructor(private toasterService: ToastrService, public formBuilder: FormBuilder, public mtlattachmentsService: AttachmentsService, public paymentTransactionservice: PaymentTransactionService, public vendorDuesService: VendorDuesService, public vendorsService: VendorsService, public customerPondsService: CustomerPondService, public itemMasterService: ItemMasterService, public businessEntityService: BusinessEntityService, public farmSiteService: FarmSiteService, public transactionsService: TransactionsService, public transactionTypeService: TransactionTypeService,
    public commonMethodsService: CommonMethodsService) {
    this.paymentTransaction = new PaymentTransactionModel();
    this.transaction = new TransactionsModel();
    this.mtlAttachmentsModel = new AttachmentsModel();
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Initializes component state and default values.
   * - Loads initial data (vendors, items) if business ID is provided.
   * - Sets up the initial line item row.
   *
   * @throws {Error} Captures and displays errors via `commonMethodsService` if initialization fails.
   */
  public async ngOnInit() {
    try {
      this.loginPersonType = localStorage.getItem('loginPersonType');
      /* user login details */
      this.LoginUserName = localStorage.getItem('LoginUserName');
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      this.isFileSelected = false;
      this.imageFile = undefined;
      this.isCleared = false;
      this.selectedbase64file = undefined;
      this.vendorNameSearch = undefined;
      this.submitButtonDisabled = false;
      $('#imageUpload').val('');
      /* user login details end */
      if (this.paramsbusinessId) {
        this.getVendorsByBusinessId();
        this.getAllItemMasterbyFarmer();
      }
      this.items = new Array();
      this.items.push({
        item_id: undefined,
        uom_id: undefined,
        unit_cost: null,
        quantity: null
      });
      this.transaction.stage = 'Growout';
      this.transaction.transaction_date = (moment(new Date()).format('DD-MMM-YYYY')).toString(); // "2020-05-20"
      this.initializedatePicker();
      this.vendorCtrl.valueChanges.subscribe(value => {
        // If the user types or clears text directly, value will be a string (not a vendor object)
        if (typeof value === 'string') {
          this.paymentTransaction.vendor_id = undefined;
        }
      });
      this.issubmitted = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method f
   * @description Getter for easy access to form controls in the HTML template.
   * @returns {any} The controls of the `registerForm`.
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @lifecycle ngOnDestroy
   * @description Lifecycle hook that is called when the component is destroyed.
   * Cancels ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests.
   */
  public ngOnDestroy(): void {
    this.vendorsService.cancelOnGoingHttpRequests();
    this.itemMasterService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.transactionsService.cancelOnGoingHttpRequests();
  }

  /**
   * @method getAllBusiness
   * @async
   * @description Fetches all farm businesses for admin login.
   * Filters businesses by type 'Farm'.
   * @side-effect Updates `businessEntities`, calls `getVendorsByBusinessId`.
   */
  public async getAllBusiness() {
    try {
      let farmBusiness = await this.businessEntityService.getAllBusiness();
      this.businessEntities = farmBusiness.filter(businessEntity => businessEntity.businessType && businessEntity.businessType.length > 0 && businessEntity.businessType[0].name && businessEntity.businessType[0].name === 'Farm');
      await this.getVendorsByBusinessId();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /** @type {FormControl} Control for vendor selection with autocomplete. */
  vendorCtrl = new FormControl<string | any>('');

  /**
   * @method displayVendor
   * @description Formats the vendor object for display in the autocomplete input.
   * @param {any | string | null} vendor - The vendor object or string.
   * @returns {string} The vendor name.
   */
  public displayVendor(vendor: any | string | null): string {
    return vendor && typeof vendor === 'object' ? vendor.name : '';
  }

  /**
   * @method filterVendors
   * @description Filters the `vendors` list based on user input.
   * @param {KeyboardEvent} [event] - The keyboard event.
   * @side-effect Updates `filteredVendorNames`.
   */
  public filterVendors(event?: KeyboardEvent): void {
    const value = (event ? (event.target as HTMLInputElement).value : this.vendorCtrl.value) || '';
    const search = typeof value === 'string' ? value.toLowerCase() : value?.name.toLowerCase();
    this.filteredVendorNames = this.vendors
      ?.filter(p => p.name.toLowerCase().includes(search)) ?? [];
  }

  /**
   * @method selectVendorOption
   * @description Handles vendor selection from the dropdown.
   * @param {any} vendor - The selected vendor object.
   * @side-effect Updates `vendorsDropdownOpen`, `vendorNameSearch`, `paymentTransaction.vendor_id`.
   */
  public selectVendorOption(vendor: any): void {
    $('.custom-select label').css({'top': 0});
    this.vendorsDropdownOpen = false;
    this.vendorNameSearch = vendor.name;
    this.paymentTransaction.vendor_id = vendor.id;
  }

  /**
   * @method getAllItemMasterbyFarmer
   * @async
   * @description Fetches all item masters (products) for the selected business.
   * Filters for stockable items (inventory) and formats item names with subtypes/UOMs.
   * @side-effect Updates `itemMasters`, `inventoryItems`.
   */
  public async getAllItemMasterbyFarmer() {
    try {
      this.itemMasters = await this.itemMasterService.getAllProductsByBusinessId(this.paramsbusinessId);
      this.inventoryItems = [];
      for (let k = 0; k < this.itemMasters.length; k++) {
        if (this.itemMasters[k].stockable == true) {
          this.inventoryItems.push(this.itemMasters[k]);
        }
      }
      if (this.inventoryItems.length > 0) {
        for (let u = 0; u < this.inventoryItems.length; u++) {
          let eachItem = this.inventoryItems[u];
          this.inventoryItems[u].item_name = eachItem.item_name + '' + (eachItem.sub_type ? '-' + eachItem.sub_type : '') + '-' + ((eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].to_value) ? '-' + (eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].to_value) : '') + ((eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].ToUOM.name) ? (eachItem && eachItem.UOMConversion.length > 0 && eachItem.UOMConversion[0].ToUOM.name) : '');
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getBusinessItemByItemId
   * @description Finds and sets the business item ID for a selected item in a specific row.
   * @param {number} index - The index of the item row.
   * @side-effect Updates `items[index].business_item_id`.
   */
  public getBusinessItemByItemId(index: number) {
    this.issubmitted = false;
    let businessItemIdarr = _.filter(this.itemMasters, (o) => {
      return o.id == this.items[index].item_id;
    });
    if (businessItemIdarr.length > 0) {
      this.items[index].business_item_id = businessItemIdarr[0].BusinessItem[0].id;
    }
  }

  /**
   * @method getItemIdsUomData
   * @description Prepares UOM data for a selected item in a specific row.
   * @param {number} index - The index of the item row.
   * @side-effect Updates `items[index].item_id`, calls `prepareUOM`.
   */
  public getItemIdsUomData(index: number) {
    this.items[index].itemUom = [];
    let itemIdarr = _.filter(this.itemMasters, (o) => {
      return o.id == this.items[index].item_id;
    });
    if (itemIdarr && itemIdarr.length > 0) {
      this.items[index].item_id = itemIdarr[0].id;
      this.prepareUOM(itemIdarr[0], index);
    }
  }

  /**
   * @method prepareUOM
   * @description Prepares the UOM dropdown list for a specific item row based on conversions.
   * @param {any} itmsArr - The item master object.
   * @param {number} index - The index of the item row.
   * @side-effect Updates `items[index].itemUom`.
   */
  public prepareUOM(itmsArr: any, index: number) {
    let uomData = []; // each time i prepare new items in onchane
    if (itmsArr && itmsArr.UOMConversion && itmsArr.UOMConversion.length > 0) {
      let uomdataArr = itmsArr.UOMConversion;
      for (let i = 0; i < uomdataArr.length; i++) {
        let tempfromobj = { 'id': uomdataArr[i].from_units_uom_id, 'name': uomdataArr[i].FromUOM.name };
        let temptoobj = { 'id': uomdataArr[i].to_units_uom_id, 'name': uomdataArr[i].ToUOM.name };
        uomData.push(tempfromobj);
        uomData.push(temptoobj);
        this.items[index].itemUom = uomData;
      }
    } else {
      uomData.push({ 'id': itmsArr.UOMMaster.id, 'name': itmsArr.UOMMaster.name });
      this.items[index].itemUom = uomData;
    }
  }

  /**
   * @method getVendorsByBusinessId
   * @async
   * @description Fetches vendors associated with the business ID.
   * @side-effect Updates `vendors`, `filteredVendorNames`, calls `setSingleValueForVendor`.
   */
  public async getVendorsByBusinessId() {
    this.issubmitted = false;
    try {
      this.vendors = await this.vendorsService.getVendorsByBusinessId(this.paramsbusinessId);
      this.filteredVendorNames = this.vendors;
      this.setSingleValueForVendor();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method setSingleValueForVendor
   * @description Auto-selects the vendor if only one exists.
   * @side-effect Updates `paymentTransaction.vendor_id`.
   */
  public setSingleValueForVendor() {
    if (this.vendors && this.vendors.length == 1) {
      this.paymentTransaction.vendor_id = Number(this.vendors[0].id); // model-binding key
    }
  }

  /**
   * @method initializedatePicker
   * @description Initializes the jQuery datepicker for the transaction date field.
   */
  public initializedatePicker() {
    let strArray = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let contract_dates = $('#startdate').datepicker({
      maxDate: new Date(),
      onSelect: function (selectedDate) {
        let parsedDate = (selectedDate.split('/')[1] + '-' + strArray[(parseInt(selectedDate.split('/')[0]) - 1)] + '-' + selectedDate.split('/')[2]);
        let option = this.id == 'startdate' ? 'minDate' : 'maxDate',
          instance = $(this).data('datepicker'),
          date = $.datepicker.parseDate(instance.settings.dateFormat || $.datepicker._defaults.dateFormat, selectedDate, instance.settings);
        contract_dates.not(this).datepicker('option', option, date);
        $('#startdate').val(parsedDate);
      }
    });
  }

  /**
   * @method todayDate
   * @description Returns the current date and time formatted as a string.
   * @returns {string} Formatted date string (YYYY-MM-DD HH:mm:ss).
   */
  public todayDate() {
    let year = new Date().getFullYear();
    let month = new Date().getMonth() + 1;
    let dateday = new Date().getDate();
    let hours = new Date().getHours();
    let minutes = new Date().getMinutes();
    let seconds = new Date().getSeconds();
    let dateStr = year + '-' + month + '-' + dateday + ' ' + hours + ':' + minutes + ':' + seconds;
    return dateStr;
  }

  /**
   * @method fileupload
   * @description Handles file selection for attachment upload.
   * Validates file size (max 2MB) and reads file as base64.
   * @param {any} event - The file input change event.
   */
  public fileupload(event: any) {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 2097152) {
          this.toasterService.error('Image size should not exceed 2MB');
          $('#imageUpload').val('');
        } else {
          this.imageFile = file;
          this.isFileSelected = true;
          this.fileName = file.name;
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            const dataresult = '' + reader.result;
            this.selectedbase64file = dataresult;
          };
          reader.onerror = () => {
          };
        }
      } else {
        this.isFileSelected = false;
      }
    }
  }

  /**
   * @method browseFile
   * @description Triggers the file input click event.
   */
  public browseFile() {
    const fileInput = document.getElementById('imageUpload') as HTMLInputElement;
    fileInput.click(); // Trigger the file input click event
  }

  /**
   * @method remove
   * @description Removes the selected file attachment.
   * Clears file-related properties.
   */
  public remove() {
    const deleteimage = confirm('Are you sure you want to delete these image?');
    if (deleteimage) {
      this.imageFile = undefined;
      this.isFileSelected = false;
      this.isCleared = true;
      this.fileName = undefined;
      this.selectedbase64file = undefined;
      $('#imageUpload').val('');
    }
  }

  /**
   * @method addNewItemButton
   * @description Adds a new line item row to the transaction.
   * Validates the last row before adding a new one.
   */
  public addNewItemButton() {
    if (this.items.length > 0) {
      if (this.items[this.items.length - 1]['item_id'] == undefined) {
        this.toasterService.error('Select item');
        return;
      } else if (this.items[this.items.length - 1]['quantity'] == undefined) {
        this.toasterService.error('Enter quantity');
        return;
      } else if (this.items[this.items.length - 1]['quantity'] <= 0) {
        this.toasterService.error('Enter valid quantity');
        return;
      } else if (this.items[this.items.length - 1]['uom_id'] == undefined) {
        this.toasterService.error('Select UOM');
        return;
      } else if (this.items[this.items.length - 1]['unit_cost'] == undefined) {
        this.toasterService.error('Enter unit cost');
        return;
      } else if (this.items[this.items.length - 1]['unit_cost'] <= 0) {
        this.toasterService.error('Enter unit cost');
        return;
      }
    }
    if (this.items.length > 0) {
      this.items.push(JSON.parse(JSON.stringify(this.items[this.items.length - 1])));
      this.totalAmount();
    }
  }

  /**
   * @method totalAmount
   * @description Calculates the total transaction amount by summing up all line items.
   * Includes GST calculation if applicable.
   * @side-effect Updates `totalamount`.
   */
  public totalAmount() {
    this.totalamount = 0;
    for (let i = 0; i < this.items.length; i++) {
      let purchase_amount;
      let eachObj = JSON.parse(JSON.stringify(this.items[i]));
      if (eachObj.quantity && eachObj.unit_cost && eachObj.gst_percent) {
        purchase_amount = ((parseFloat('' + eachObj.quantity) * parseFloat('' + eachObj.unit_cost) * parseFloat('' + eachObj.gst_percent)) / 100) + parseFloat('' + eachObj.quantity) * parseFloat('' + eachObj.unit_cost);
        this.totalamount += purchase_amount; // test key
      } else {
        purchase_amount = parseFloat('' + eachObj.quantity) * parseFloat('' + eachObj.unit_cost);
        this.totalamount += purchase_amount; // test key
      }
    }
  }

  /**
   * @method numberValidation
   * @description Validates input to allow only numbers and a single decimal point.
   * Prevents invalid characters.
   * @param {any} event - The keyboard event.
   */
  public numberValidation(event: any) {
    const key = event.key;
    const value = event.target.value;
    // Allow numbers, the dot (decimal point), and the Backspace key
    if (
      !/^\d$/.test(key) && // Check if the entered character is a number
      key !== '.' && // Check if the entered character is a dot (decimal point)
      key !== 'Backspace' // Check if the Backspace key was pressed
    ) {
      event.preventDefault();
    }

    // Allow only one dot (decimal point)
    if (key === '.' && value.includes('.')) {
      event.preventDefault();
    }

    // Disallow the first letter being a dot
    if (key === '.' && value === '') {
      event.preventDefault();
    }
  }

  /**
   * @method removeItemCard
   * @description Removes a line item row from the transaction.
   * Prevents removing the last remaining row.
   * @param {number} i - The index of the row to remove.
   * @returns {boolean} True if removed, false otherwise.
   */
  public removeItemCard(i: number) {
    if (this.items.length == 1) {
      this.toasterService.error('Can\'t delete the item card when there is only one card');
      return false;
    } else {
      this.items.splice(i, 1);
      this.toasterService.success('Card deleted successfully', '', {
        timeOut: 2000
      });
      this.totalAmount();
      return true;
    }
  }

  /**
   * @method inventoryTransaction
   * @async
   * @description Validates and submits the multi-line purchase transaction.
   * Handles validation for all items, vendor selection, and invoice details.
   * Creates transaction records and optionally triggers payment recording.
   */
  public async inventoryTransaction() {
    this.transaction.transaction_date = moment(this.transactionDateControl.value).format('YYYY-MM-DD HH:mm:ss');
    this.issubmitted = false;
    if (!this.paramsbusinessId) {
      this.toasterService.clear();
      this.toasterService.error('Please select farm');
      return false;
    }
    if (this.paramsfarmsiteId == null || this.paramsfarmsiteId == 'undefined') {
      this.toasterService.clear();
      this.toasterService.error('Please select farmsite');
      return false;
    }
    if (this.itemMasters.length == 0) {
      this.toasterService.clear();
      this.toasterService.warning('No item available for this business ');
      return false;
    }
    if (!this.transaction.transaction_date) {
      this.toasterService.clear();
      this.toasterService.error('Please select date');
      return false;
    }
    if (!this.transaction.stage) {
      this.toasterService.clear();
      this.toasterService.error('Please select stage');
      return false;
    }
    if (this.items[this.items.length - 1]['item_id'] == undefined) {
      this.toasterService.clear();
      this.toasterService.error('Select item');
      return;
    } else if (this.items[this.items.length - 1]['quantity'] == undefined) {
      this.toasterService.clear();
      this.toasterService.error('Enter quantity');
      return;
    } else if (this.items[this.items.length - 1]['quantity'] <= 0) {
      this.toasterService.clear();
      this.toasterService.error('Enter valid quantity');
      return;
    } else if (this.items[this.items.length - 1]['uom_id'] == undefined) {
      this.toasterService.clear();
      this.toasterService.error('Select UOM');
      return;
    } else if (this.items[this.items.length - 1]['unit_cost'] == undefined) {
      this.toasterService.clear();
      this.toasterService.error('Enter unit cost');
      return;
    } else if (this.items[this.items.length - 1]['unit_cost'] <= 0) {
      this.toasterService.clear();
      this.toasterService.error('Enter unit cost');
      return;
    }
    this.submitButtonDisabled = true;
    const addTransactionReq = [];
    for (let i = 0; i < this.items.length; i++) {
      this.transaction = JSON.parse(JSON.stringify(this.transaction));
      this.transaction.business_id = this.paramsbusinessId;
      this.transaction.farm_site_id = this.paramsfarmsiteId;
      this.transaction.transaction_type_id = 1;
      this.transaction.farm_location = this.locationName;
      this.transaction.business_name = this.businessName;
      this.transaction.updated_by = localStorage.personID;
      this.transaction.created_name = this.LoginUserName;
      this.transaction.updated_name = this.LoginUserName;
      this.transaction.created_by = localStorage.personID;
      this.transaction.created_date = this.todayDate();
      this.transaction.updated_date = this.todayDate();
      this.transaction.item_id = this.items[i].item_id;
      this.transaction.business_item_id = Number(this.items[i].business_item_id);
      this.transaction.unit_cost = Number(this.items[i].unit_cost);
      this.transaction.gst_percent = Number(this.items[i].gst_percent || 0);
      this.transaction.gst_value = Number((this.items[i].quantity * this.items[i].unit_cost * this.items[i].gst_percent) / 100);
      this.transaction.value = Number(this.items[i].quantity && this.items[i].unit_cost && this.items[i].gst_percent ? (((this.items[i].quantity * this.items[i].unit_cost * this.items[i].gst_percent) / 100) + (this.items[i].quantity * this.items[i].unit_cost)) : this.items[i].quantity * this.items[i].unit_cost);
      if (this.items[i].itemUom[0].uomadded == true && this.items[i].uom_id == 3) {
        this.transaction.quantity = Number(this.items[i].quantity) / 1000;
        this.transaction.uom_id = 1;
      } else {
        this.transaction.quantity = Number(this.items[i].quantity);
        this.transaction.uom_id = Number(this.items[i].uom_id);
      }
      this.paymentTransaction.vendor_id = this.paymentTransaction.vendor_id;
      if (this.loginPersonType != 'EMPLOYEE') {
        delete this.transaction.business_id;
        delete this.transaction.business_name;
        delete this.transaction.created_name;
        delete this.transaction.updated_name;
        delete this.transaction.created_by;
        delete this.transaction.updated_by;
      }
      addTransactionReq.push(this.transaction);
    }
    this.paymentDetails = [];
    if (this.paymentTransaction.invoice_number) {
      if (this.paymentTransaction.invoice_number.length > 15) {
        this.toasterService.clear();
        this.toasterService.error('Invoice number should be 15 characters');
        this.submitButtonDisabled = false;
        return;
      }
    }
    if (this.paymentTransaction.invoice_number && (this.vendors.length == 0)) {
      this.toasterService.clear();
      this.toasterService.warning('Vendors are not associated for this business');
      return false;
    } else if (this.paymentTransaction.invoice_number && (Number(this.paymentTransaction.paid_amount) && !Number.isFinite(this.paymentTransaction.vendor_id))) {
      this.toasterService.clear();
      this.toasterService.error('Please select vendor');
      this.submitButtonDisabled = false;
      return;
    }
    if ((Number(this.paymentTransaction.paid_amount)) && (this.vendors.length == 0)) {
      this.toasterService.clear();
      this.toasterService.warning('Vendors are not associated for this business');
      this.submitButtonDisabled = false;
      return false;
    } else if ((Number(this.paymentTransaction.paid_amount)) && (Number(this.paymentTransaction.paid_amount) && !Number.isFinite(this.paymentTransaction.vendor_id))) {
      this.toasterService.clear();
      this.toasterService.error('Please select vendor');
      this.submitButtonDisabled = false;
      return false;
    }
    if (this.transaction.notes && this.transaction.notes.length > 200) {
      this.toasterService.clear();
      this.toasterService.error('Maximum 200 characters allowed for description');
      this.submitButtonDisabled = false;
      return false;
    }
    if ((this.paymentTransaction.vendor_id != undefined || this.paymentTransaction.paid_amount)) {
      this.getPaymentTransaction(addTransactionReq);
    } else {
      try {
        this.transactions = await this.transactionsService.createTransaction(addTransactionReq);
        if (this.transactions) {
          this.latestTransactionData = this.transactions;
          let image = this.imageFile;
          let transactionIds =  new Array();
          for (let i=0;i < this.latestTransactionData.transactionResult[0].transactions.length; i++) {
            transactionIds.push(this.latestTransactionData.transactionResult[0].transactions[i].id);
          }
          if (this.isFileSelected) {
            const upload = this.transactionsService.uploadFileToS3Bucket(this.imageFile, this.latestTransactionData.business_name, 'Material_Tx', `MM-${this.imageFile.name}`);
            upload.subscribe((res) => {
              if (res) {
                for(let i=0; i < transactionIds.length;i++) {
                  this.mtlAttachmentsModel.file_name = image.name;
                  this.mtlAttachmentsModel.file_type = image.type.split('/')[1];
                  this.mtlAttachmentsModel.attachment_path = Object.values(res)[0];
                  this.mtlAttachmentsModel.transaction_id = Number(transactionIds[i]);
                  this.mtlattachmentsService.create(this.mtlAttachmentsModel);
                }
              } else {
                this.toasterService.error('File upload failed--')
                this.submitButtonDisabled = false;
              }
            })
          }
          this.toasterService.success('Transaction created successfully', '', {
            timeOut: 2000
          });
          $('#imageUpload').val('');
          this.vendorNameSearch = undefined;
          this.imageFile = undefined;
          this.isFileSelected = false;
          this.isCleared = false;
          this.selectedbase64file = undefined;
          this.submitted = false;
          this.fileName = undefined;
          this.issubmitted = true;
          this.totalamount = 0;
          this.submitButtonDisabled = false;
          this.items = new Array();
          this.items.push({
            item_id: undefined,
            uom_id: undefined,
            unit_cost: null,
            quantity: null,
            gst_percent: null
          });
          this.transaction.transaction_date = moment(new Date()).format('DD-MMM-YYYY'); // "2020-05-20"
          this.transactionDateControl.setValue(new Date());
          this.vendorCtrl.reset(); // Clear input field
          this.filteredVendorNames = []; // Clear filtered list (optional)
          this.vendorNameSearch = ''; // Clear if you're using this to bind anywhere
          this.paymentTransaction.vendor_id = null; // Clear selected vend
          this.vendorCtrl.setValue(''); // instead of reset()
          this.vendorCtrl.markAsPristine(); // optional: reset touched state
        }
      } catch (error) {
        this.commonMethodsService.userFriendlyAlertMessages(error);
        this.submitButtonDisabled = false;
      }
    }
  }

  /**
   * @method getPaymentTransaction
   * @async
   * @description Records a payment transaction associated with the multi-line purchase.
   * @param {any} transaction - The transaction details array.
   */
  public async  getPaymentTransaction(transaction: any) {
    const quantity = [];
    const uomIds = [];
    const valuesArr = [];
    if (transaction && transaction.length > 0) {
      for (let i = 0; i < transaction.length; i++) {
        quantity.push(transaction[i].quantity);
        uomIds.push(Number(transaction[i].uom_id));
        valuesArr.push(Number(transaction[i].value));
      }
    }
    this.paymentTransaction.value = valuesArr;
    this.paymentTransaction.quantity = quantity;
    this.paymentTransaction.uom_id = uomIds;
    this.paymentTransaction.purchase_amount = Number(this.totalamount);
    this.paymentTransaction.business_id = this.paramsbusinessId;
    this.paymentTransaction.farm_site_id = this.paramsfarmsiteId;
    this.paymentTransaction.value = valuesArr;
    if (this.paymentTransaction.invoice_number) {
      this.paymentTransaction.invoice_number = this.paymentTransaction.invoice_number;
    }
    if (this.paymentTransaction.paid_amount == null) {
      this.paymentTransaction.paid_amount = 0;
    } else {
      this.paymentTransaction.paid_amount = Number(this.paymentTransaction.paid_amount);
    }
    if (this.paymentTransaction.paid_amount == this.totalamount) { //
      this.paymentTransaction.payment_status = 'Fully';
    } else if (this.paymentTransaction.paid_amount < (this.totalamount)) {
      this.paymentTransaction.payment_status = 'Partially';
    } else if (this.paymentTransaction.paid_amount > (this.totalamount)) {
      this.toasterService.warning('Amount should be lessthan value or same');
      this.submitButtonDisabled = false;
      return false;
    }
    this.paymentTransaction.date = moment(this.transactionDateControl.value).format('YYYY-MM-DD')
    this.paymentTransaction.transaction_type_id = 1;
    this.paymentTransaction.vendor_id = Number(this.paymentTransaction.vendor_id);
    try {
      this.transactions = await this.transactionsService.createTransaction(transaction);
      if (this.transactions) {
        this.latestTransactionData = this.transactions;
        let image = this.imageFile;
        let transactionIds =  new Array();
        let itemIds = new Array();
        for (let i=0;i < this.latestTransactionData.transactionResult.length; i++) {
          transactionIds.push(this.latestTransactionData.transactionResult[i].transactions[0].id);
          itemIds.push(this.latestTransactionData.transactionResult[i].transactions[0].item_id)
        }
        if (this.isFileSelected) {
          const upload = this.transactionsService.uploadFileToS3Bucket(this.imageFile, this.latestTransactionData.business_name, 'Material_Tx', `MM-${this.imageFile.name}`);
          upload.subscribe((res) => {
            if (res) {
              for(let i=0; i < transactionIds.length;i++) {
                this.mtlAttachmentsModel.file_name = image.name;
                this.mtlAttachmentsModel.file_type = image.type.split('/')[1];
                this.mtlAttachmentsModel.attachment_path = Object.values(res)[0];
                this.mtlAttachmentsModel.transaction_id = Number(transactionIds[i]);
                this.mtlattachmentsService.create(this.mtlAttachmentsModel);
              }
            } else {
              this.toasterService.error('File upload failed--');
              this.submitButtonDisabled = false;
            }
          })
        }
        this.toasterService.success('Transaction created successfully', '', {
          timeOut: 2000
        });
        this.imageFile = undefined;
        this.isFileSelected = false;
        this.isCleared = false;
        this.selectedbase64file = undefined;
        this.fileName = undefined;
        this.submitted = false;
        this.issubmitted = true;
        this.totalamount = 0;
        this.submitButtonDisabled = false;
        this.items = new Array();
        this.items.push({
          item_id: undefined,
          uom_id: undefined,
          unit_cost: null,
          quantity: null,
          gst_percent: null
        });
        this.transaction.transaction_date = moment(new Date()).format('DD-MMM-YYYY'); // "2020-05-20"
        this.transactionDateControl.setValue(new Date());
        this.paymentTransaction.material_transaction_id = transactionIds;
        this.paymentTransaction.item_id = itemIds;
        this.paymentTransaction = await this.paymentTransactionservice.addPaymentTransaction(this.paymentTransaction);
        this.paymentDetails = this.paymentTransaction;
        this.paymentTransaction.vendor_id = undefined;
        this.paymentTransaction.invoice_number = '';
        this.paymentTransaction.paid_amount = undefined;
        this.vendorNameSearch = undefined;
        this.submitButtonDisabled = false;
        this.vendorCtrl.reset(); // Clear input field
        this.filteredVendorNames = []; // Clear filtered list (optional)
        this.vendorNameSearch = ''; // Clear if you're using this to bind anywhere
        this.paymentTransaction.vendor_id = null; // Clear selected ven
        this.vendorCtrl.setValue(''); // instead of reset()
        this.vendorCtrl.markAsPristine(); // optional: reset touched state
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.submitButtonDisabled = false;
    }
  }

  /**
   * @method clearTransactionData
   * @description Clears all transaction form data and resets the component state.
   * Called when the cancel button is clicked.
   * @param {any} $event - The click event.
   */
  public clearTransactionData($event: any) {
    if (this.loginPersonType != 'Farmer' && this.loginPersonType != 'Farm Manager' && this.loginPersonType != 'Business Admin' && this.loginUserBusinessCategory != 'Farm') {
      this.vendors = [];
    }
    this.vendorNameSearch = undefined;
    this.submitButtonDisabled = false;
    this.transaction = new TransactionsModel();
    this.paymentTransaction = new PaymentTransactionModel();
    this.mtlAttachmentsModel = new AttachmentsModel();
    this.ngOnInit();
    this.totalamount = 0;
    $event.stopPropagation();
    $event.preventDefault();
    this.vendorCtrl.reset(); // Clear input field
    this.filteredVendorNames = []; // Clear filtered list (optional)
    this.vendorNameSearch = ''; // Clear if you're using this to bind anywhere
    this.paymentTransaction.vendor_id = null; // Clear selected vend
    this.vendorCtrl.setValue(''); // instead of reset()
    this.vendorCtrl.markAsPristine(); // optional: reset touched state
  }
}
